import { useContext, useEffect, useMemo } from 'react';
import { fetchRobotById, fetchRobotCatalog, fetchRobotCloudProjectSettings, rcDataContext, selectRobotById, selectRobotCatalog,
    selectRobotCloudProjectSettings } from '@moderepo/mode-data-state-robot-cloud';
import { RCProjectSettings, RobotDefinition, RobotInfo } from '@moderepo/mode-apis';
import { getRobotDefinitionByRobotClass } from '@moderepo/mode-app-components';


// This file contains custom hooks for selecting/fetching data from data state. On each page or container, we usually select some
// data we need from the data state. And if the data is not fetched, we dispatch an action to fetch the data and when the data is ready,
// we make try to select the data again. We do this a lot therefore, we will create custom hooks to handle these select/fetch data so
// that we can do everything with just 1 line



/**
 * A custom hook to select robot catalog from the data state and fetch robot catalog data if data is not loaded.
 * @param projectId
 * @param homeId
 * @param robotId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectRobotCatalog = (
    projectId: number | undefined, fetchLatest?: boolean | undefined, showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly RobotDefinition[] | undefined => {
    const { state, dispatch } = useContext(rcDataContext);
    const resultData = projectId ? selectRobotCatalog(state, projectId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (projectId && fetchLatest) {
            dispatch(fetchRobotCatalog(projectId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, projectId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (projectId && !resultData) {
            dispatch(fetchRobotCatalog(projectId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, projectId, resultData, showLoading, suppressErrors]);

    return resultData;
};


/**
 * A custom hook to select a robot by ID from the data state and fetch robot data if data is not loaded.
 * @param projectId
 * @param homeId
 * @param robotId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectRobot = (
    projectId: number | undefined, homeId: number | undefined, robotId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): RobotInfo | undefined => {
    const { state, dispatch } = useContext(rcDataContext);
    const resultData = homeId && robotId ? selectRobotById(state, homeId, robotId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (homeId && robotId && projectId && fetchLatest) {
            dispatch(fetchRobotById(projectId, homeId, robotId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, homeId, projectId, robotId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (homeId && robotId && projectId && !resultData) {
            dispatch(fetchRobotById(projectId, homeId, robotId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, homeId, projectId, resultData, robotId, showLoading, suppressErrors]);

    return resultData;
};



/**
 * A custom hook to select a robot definition for a given robotId from the data state and fetch robot definition data if data is not loaded.
 * @param projectId
 * @param homeId
 * @param robotId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectRobotDefinitionById = (
    projectId: number | undefined, homeId: number, robotId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): RobotDefinition | undefined => {
    const robotCatalog = useSelectRobotCatalog(projectId, fetchLatest, showLoading, suppressErrors);
    const robot = useSelectRobot(projectId, homeId, robotId, fetchLatest, showLoading, suppressErrors);

    const resultData = useMemo(() => {
        if (robotCatalog && robot) {
            return getRobotDefinitionByRobotClass(robotCatalog, robot.robotClass);
        }
        return undefined;
    }, [robot, robotCatalog]);

    return resultData;
};



/**
 * A custom hook to select RC project settings for a given projectId from the data state and fetch project settings data if data is not loaded.
 * @param projectId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectRCProjectSettings = (
    projectId: number | undefined, fetchLatest: boolean = false,
): RCProjectSettings | undefined => {
    const { state, dispatch } = useContext(rcDataContext);
    const resultData = projectId ? selectRobotCloudProjectSettings(state, projectId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (projectId && fetchLatest) {
            dispatch(fetchRobotCloudProjectSettings(projectId)).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, fetchLatest, projectId]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (projectId && !resultData) {
            dispatch(fetchRobotCloudProjectSettings(projectId)).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, projectId, resultData]);

    return resultData;
};
